import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./index.scss";
import MdaePopup, {
  MdaePopupContent,
  MdaePopupFooter,
  MdaePopupFooterEndButtons,
  MdaePopupFooterStartButtons,
  MdaePopupTitle,
} from "../common_widgets/popup";
import {
  downloadFormData as defaultDownloadFormData,
  LAYOUT,
  SET_DOWNLOAD_POPUP_FORM_DATA,
  TOGGLE_DOWNLOAD_POP_UP,
} from "./reducers/download-liveboard-reducer";
import { HIDE_LOADER, SHOW_LOADER } from "../mdae-common-reducer";
import { EXPORT_THOUGHTSPOT_LIVEBOARD } from "./services/export-thoughtspot-liveboard";
import MdaeButton from "../common_widgets/button";
import MdaeCheckBox from "../common_widgets/check-box";
import MdaeRadioGroup from "../common_widgets/radio-group";
import MdaeTextArea from "../common_widgets/text-area";
import { isEqual } from "lodash";
import {
  CURRENT_LIVEBOARD,
  SET_AI_SUMMARY,
} from "./reducers/ai-summary-reducer";
import { callOperation } from "async-ops";
import { GET_AI_SUMMARY } from "./services/get-ai-summary";

export const toggleDownloadLiveBoardPopup = (
  isDownloadPopupOpen,
  dispatch,
  currentLiveBoardId,
  currentLiveBoardName
) => {
  dispatch({
    type: TOGGLE_DOWNLOAD_POP_UP,
    data: {
      isDownloadPopupOpen: !isDownloadPopupOpen,
    },
  });
  dispatch({
    type: CURRENT_LIVEBOARD,
    data: {
      liveBoardId: currentLiveBoardId,
      liveBoardName: currentLiveBoardName,
    },
  });
  dispatch({
    type: SET_DOWNLOAD_POPUP_FORM_DATA,
    data: {
      downloadFormData: defaultDownloadFormData,
    },
  });
};

const DownloadLiveBoardPopup: React.FC = () => {
  const dispatch = useDispatch();

  const isDownloadPopupOpen = useSelector(
    (state: any) => state.downloadPopupReducer.isDownloadPopupOpen
  );
  const downloadFormData = useSelector(
    (state: any) => state.downloadPopupReducer.downloadFormData
    
  );
  const liveBoardId = useSelector(
    (state: any) => state.aiSummaryReducer.liveBoardId
  );
  const liveBoardName = useSelector(
    (state: any) => state.aiSummaryReducer.liveBoardName
  );
  const aiSummaryText = useSelector(
    (state: any) => state.aiSummaryReducer.aiSummary?.summaryText
    
  );
  const closePopupHandler = useCallback(() => {
    dispatch({
      type: TOGGLE_DOWNLOAD_POP_UP,
      data: { isDownloadPopupOpen: false },
    });
  }, [dispatch]);

  const handleLayoutChange = useCallback(
    (e) => {
      if (e.event) {
        dispatch({
          type: SET_DOWNLOAD_POPUP_FORM_DATA,
          data: {
            downloadFormData: { orientation: e.value },
          },
        });
      }
    },
    [dispatch]
  );

  const handleFooterTextChange = useCallback(
    (e) => {
      if (e.event) {
        dispatch({
          type: SET_DOWNLOAD_POPUP_FORM_DATA,
          data: {
            downloadFormData: { footerText: e.value },
          },
        });
      }
    },
    [dispatch]
  );

  const handleIncludeAISummaryChange = useCallback(
    (e) => {
      if (e.event) {
        dispatch({
          type: SET_DOWNLOAD_POPUP_FORM_DATA,
          data: {
            downloadFormData: { includeAISummary: e.value },
          },
        });
      }
    },
    [dispatch]
  );

  const handleCheckboxChange = useCallback(
    (field, value) => {
      const updatedData: { [key: string]: any; isAllChecked?: boolean } = {
        [field]: value,
      };
      if (
        [
          "isProfessionalChecked",
          "isOutpatientChecked",
          "isInpatientChecked",
        ].includes(field)
      ) {
        const allChecked =
          (field === "isProfessionalChecked"
            ? value
            : downloadFormData.isProfessionalChecked) &&
          (field === "isOutpatientChecked"
            ? value
            : downloadFormData.isOutpatientChecked) &&
          (field === "isInpatientChecked"
            ? value
            : downloadFormData.isInpatientChecked);
        updatedData.isAllChecked = allChecked;
      }
      dispatch({
        type: SET_DOWNLOAD_POPUP_FORM_DATA,
        data: {
          downloadFormData: updatedData,
        },
      });
    },
    [dispatch, downloadFormData]
  );

  const exportLiveboard = useCallback(() => {
    dispatch({ type: SHOW_LOADER });
    const requestData = {
      liveBoardId,
      liveBoardName,
      vizId: null,
      orientation: downloadFormData.orientation,
      footerText: downloadFormData.footerText,
      includeAISummary: downloadFormData.includeAISummary,
      isAllChecked: downloadFormData.isAllChecked,
      isProfessionalChecked: downloadFormData.isProfessionalChecked,
      isOutpatientChecked: downloadFormData.isOutpatientChecked,
      isInpatientChecked: downloadFormData.isInpatientChecked,
      layoutType: downloadFormData.isAllChecked ? "PINBOARD" : "VISUALIZATION",
    };

    callOperation(EXPORT_THOUGHTSPOT_LIVEBOARD, requestData)
      .then((blob) => {
        const fileURL = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = fileURL;
        link.download =
          liveBoardName === "auditroi"
            ? "ROI Summary - Internal Audits.pdf"
            : "ROI Summary - Payer Audits.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(fileURL);

        if (downloadFormData.includeAISummary) {
          callOperation(GET_AI_SUMMARY, { liveBoardId }).then((response) => {
            dispatch({
              type: SET_AI_SUMMARY,
              data: {
                summaryText: response.aiSummary.summaryText,
                createdDate: response.aiSummary.createdDate,
              },
              liveBoardId,
            });
            toggleDownloadLiveBoardPopup(true, dispatch, "", "");
          });
        }
      })
      .catch((error) => console.error("File download failed", error))
      .finally(() => dispatch({ type: HIDE_LOADER }));
  }, [dispatch, liveBoardId, liveBoardName, downloadFormData]);

  const popupTitleRender = useCallback(() => {
    return (
      <MdaePopupTitle closePopupHandler={closePopupHandler}>
        Download
      </MdaePopupTitle>
    );
  }, [closePopupHandler]);

  const popupContentRender = useCallback(() => {
    return (
      <MdaePopupContent>
        <div className="thoughtspot-liveboard-download-popup">
          <label>Layout</label>
          <div className="radio-button section">
            <MdaeRadioGroup
              id="layout-radio"
              items={LAYOUT}
              width={"150px"}
              valueExpr="value"
              displayExpr="text"
              value={downloadFormData.orientation}
              onValueChanged={handleLayoutChange}
            />
          </div>
          <div className="section">
            <label>Select tabs to download</label>
            <div className="checkbox">
              <MdaeCheckBox
                defaultValue={false}
                id="isAllChecked"
                text="All"
                value={downloadFormData.isAllChecked}
                onValueChanged={(e) =>
                  e.event &&
                  dispatch({
                    type: SET_DOWNLOAD_POPUP_FORM_DATA,
                    data: {
                      downloadFormData: {
                        isAllChecked: e.value,
                        isProfessionalChecked: e.value,
                        isOutpatientChecked: e.value,
                        isInpatientChecked: e.value,
                      },
                    },
                  })
                }
              />
              {["Professional", "Outpatient", "Inpatient"].map((type) => (
                <MdaeCheckBox
                  key={type}
                  defaultValue={false}
                  id={`is${type}Checked`}
                  text={type}
                  value={downloadFormData[`is${type}Checked`]}
                  onValueChanged={(e) =>
                    e.event && handleCheckboxChange(`is${type}Checked`, e.value)
                  }
                />
              ))}
            </div>
          </div>
          <div className="section">
            <label>Footer text</label>
            <MdaeTextArea
              id="footerText"
              placeholder="Footer text"
              value={downloadFormData.footerText}
              onValueChanged={handleFooterTextChange}
              mdaeTextAreaTitle=""
            />
          </div>
          <div className="widget-separation-margin-top">
            <MdaeCheckBox
              defaultValue={false}
              id="chkshowAsPopup"
              text="Include AI Summary"
              value={downloadFormData.includeAISummary}
              onValueChanged={handleIncludeAISummaryChange}
              disabled={!aiSummaryText?.trim()}
            />
          </div>
        </div>
      </MdaePopupContent>
    );
  }, [
    downloadFormData,
    handleLayoutChange,
    handleFooterTextChange,
    handleIncludeAISummaryChange,
    handleCheckboxChange,
    dispatch,
  ]);

  const popupFooterRender = useCallback(() => {
    return (
      <MdaePopupFooter>
        <MdaePopupFooterStartButtons>
          <MdaeButton
            id="btnBulkUpdateCancel"
            role="secondary"
            onClick={() => toggleDownloadLiveBoardPopup(true, dispatch, "", "")}
          >
            Cancel
          </MdaeButton>
        </MdaePopupFooterStartButtons>
        <MdaePopupFooterEndButtons>
          <MdaeButton
            id="btnBulkUpdateSaveAndClose"
            onClick={exportLiveboard}
            role="primary"
          >
            Download
          </MdaeButton>
        </MdaePopupFooterEndButtons>
      </MdaePopupFooter>
    );
  }, [dispatch, exportLiveboard]);

  return (
    <MdaePopup
      deferRendering={false}
      id="downloadPopup"
      height={600}
      width={600}
      mdaeTitleRender={popupTitleRender}
      mdaeDataUpdaterConfig={[
        {
          actionType: TOGGLE_DOWNLOAD_POP_UP,
          reducerName: "downloadPopupReducer",
          widgetOptionBindings: { visible: "isDownloadPopupOpen" },
        },
      ]}
      mdaeContentRender={popupContentRender}
      mdaeFooterRender={popupFooterRender}
    />
  );
};

export default DownloadLiveBoardPopup;
